import { Link } from "react-router-dom";
import { Coins, Gift, Radio, Sparkles, TrendingUp, Users, ArrowRight, HelpCircle } from "lucide-react";

/**
 * Creator FAQ — public page that reflects the Feb 2026 monetization rules:
 *   • 100% subscription revenue to platform.
 *   • Tips / virtual gifts split: platform 45% / creator 55%.
 *   • No 100-coin signup grant.
 *   • 30-day free trial of Standard membership for new members.
 *   • Golden Ticket free spin, Sundays only.
 */
const SECTIONS = [
  {
    icon: TrendingUp,
    title: "How do I make money on My Panda X?",
    body: (
      <>
        <p className="text-zinc-300 leading-relaxed mb-3">
          Fans support you two ways:
        </p>
        <ul className="list-disc pl-5 space-y-1.5 text-zinc-300">
          <li><strong>Tips & virtual gifts</strong> sent during live streams, in DMs, or on your posts.</li>
          <li><strong>Subscriptions</strong> to your creator profile or private communities (coming soon for creators).</li>
        </ul>
      </>
    ),
  },
  {
    icon: Coins,
    title: "What's the revenue split on tips and gifts?",
    body: (
      <>
        <p className="text-zinc-300 leading-relaxed mb-3">
          For every tip or virtual gift a fan sends you:
        </p>
        <ul className="list-disc pl-5 space-y-1.5 text-zinc-300">
          <li><strong>You keep 55%.</strong></li>
          <li>My Panda X takes 45% to cover payments, hosting, moderation, and platform growth.</li>
        </ul>
        <p className="text-zinc-500 text-sm mt-3">
          The split is applied automatically at payout time. There are no extra
          processing fees on top of the 45% platform fee.
        </p>
      </>
    ),
  },
  {
    icon: Sparkles,
    title: "What about subscription revenue?",
    body: (
      <>
        <p className="text-zinc-300 leading-relaxed">
          <strong>Subscription revenue is 100% platform.</strong> Any future
          platform-level membership revenue funds the free 30-day trial that every
          new member receives, plus infrastructure and moderation.
        </p>
        <p className="text-zinc-500 text-sm mt-3">
          When creator-side subscriptions launch we'll publish a separate revenue
          share for that product. Until then, tips and gifts are your income
          channel and they always stay at 55% to you.
        </p>
      </>
    ),
  },
  {
    icon: Gift,
    title: "How do I get paid?",
    body: (
      <>
        <p className="text-zinc-300 leading-relaxed mb-2">
          Complete Stripe Connect Express onboarding from your{" "}
          <Link to="/creator" className="text-rose-400 hover:underline">Creator Dashboard</Link>.
          Once your Connect account is verified, tips are paid out directly to
          your bank on Stripe's standard schedule (usually 2 business days after
          the charge clears).
        </p>
      </>
    ),
  },
  {
    icon: Radio,
    title: "Are there rules about what I can stream?",
    body: (
      <>
        <p className="text-zinc-300 leading-relaxed">
          Yes — no minors, no non-consensual content, no illegal content, no
          hate. Our AI moderator plus a human review team enforces this on every
          post and stream. Repeat violators are permanently removed and payouts
          are frozen.
        </p>
      </>
    ),
  },
  {
    icon: Users,
    title: "What's the deal with the Golden Ticket?",
    body: (
      <>
        <p className="text-zinc-300 leading-relaxed">
          The Golden Ticket is a free coin spin for paying members — <strong>available only on Sundays</strong>.
          It's a small thank-you, not a payout channel. If you want to reward
          your own fans with prizes, set up a Live Wheel from the Go Live screen.
        </p>
      </>
    ),
  },
];

export default function FaqCreators() {
  return (
    <div className="min-h-screen bg-black text-white">
      <div className="max-w-3xl mx-auto px-5 sm:px-8 py-16" data-testid="faq-creators-page">
        <Link to="/about" className="text-xs uppercase tracking-widest text-zinc-500 hover:text-rose-500 inline-flex items-center gap-1 mb-6">
          ← Back to About
        </Link>

        <div className="mb-10">
          <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.3em] text-rose-500 border border-rose-500/30 px-3 py-1 mb-4">
            <HelpCircle size={12} /> Creator FAQ
          </div>
          <h1 className="font-display text-4xl sm:text-5xl leading-tight mb-3">
            Everything you need to know before you go live.
          </h1>
          <p className="text-zinc-400 leading-relaxed">
            Straight-talk answers on payouts, revenue share, and the platform
            rules that keep My Panda X sustainable for the long haul.
          </p>
        </div>

        <div className="space-y-8" data-testid="faq-creators-sections">
          {SECTIONS.map(({ icon: Icon, title, body }) => (
            <section key={title} className="border-l-2 border-rose-600/40 pl-5 py-1">
              <div className="flex items-center gap-2 mb-3">
                <Icon size={18} className="text-rose-500" />
                <h2 className="font-display text-xl">{title}</h2>
              </div>
              <div className="text-sm">{body}</div>
            </section>
          ))}
        </div>

        <div className="mt-14 border border-white/10 bg-white/[0.02] p-6">
          <div className="text-[10px] uppercase tracking-[0.3em] text-amber-400 mb-2">Still stuck?</div>
          <div className="font-display text-2xl mb-3">Ask the Panda team.</div>
          <p className="text-sm text-zinc-400 mb-4">
            DM <span className="text-rose-400">@panda</span> from your inbox and
            we'll get back to you within one business day.
          </p>
          <Link
            to="/inbox"
            className="inline-flex items-center gap-2 btn-crimson rounded-none px-5 py-3 text-sm"
            data-testid="faq-creators-contact"
          >
            Open Inbox <ArrowRight size={14} />
          </Link>
        </div>

        <div className="mt-10 flex flex-wrap gap-3 text-xs">
          <Link to="/faq/members" className="text-zinc-500 hover:text-rose-500" data-testid="faq-creators-member-link">
            Member FAQ →
          </Link>
          <Link to="/about" className="text-zinc-500 hover:text-rose-500">
            About My Panda X →
          </Link>
        </div>
      </div>
    </div>
  );
}
